import { useState } from "react";
import { useTellescopeMutation } from "../../use-tellescope-mutation";
import { useTellescopeQueryClient } from "../../use-tellescope-query-client";
import { SupportedSession } from "../../../types";

interface ScriptSureUserLookupResponse {
  data: {
    found: boolean;
    scriptsureId?: string | null;
    prescriber?: boolean | null;
    error?: string;
  };
}

const VALID_DATA_KEYS = new Set([
  "found",
  "scriptsureId",
  "prescriber",
  "error",
] satisfies (keyof ScriptSureUserLookupResponse["data"])[]);
const isScriptSureUserLookupResponse = (res: unknown): res is ScriptSureUserLookupResponse => {
  if (typeof res !== "object" || res === null) return false;
  if (!("data" in res)) return false;
  if (typeof res.data !== "object" || res.data === null) return false;
  const dataEntries = Object.entries(res.data);
  const allAreValid = dataEntries.every(([key, value]) => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    if (!VALID_DATA_KEYS.has(key as keyof ScriptSureUserLookupResponse["data"])) return false;
    switch (key) {
      case "found":
        return typeof value === "boolean";
      case "scriptsureId":
        return typeof value === "string" || value === null;
      case "prescriber":
        return typeof value === "boolean" || value === null;
      case "error":
        return typeof value === "string";
    }
  });
  return allAreValid;
};

type ErrorType =
  | "user-not-found"
  | "user-missing-email-address"
  | "org-missing-scriptsure"
  | "no-scriptsure-user-found"
  | "no-userid-provided"
  | "unknown";

const convertErrorStringToErrorType = (errorString: string): ErrorType => {
  if (errorString.includes("User not found")) {
    return "user-not-found";
  } else if (errorString.includes("User does not have an email address")) {
    return "user-missing-email-address";
  } else if (errorString.includes("Organization does not have ScriptSure configured")) {
    return "org-missing-scriptsure";
  }
  return "unknown";
};

export interface UseLinkScriptSurePrescriberIdOptions {
  session?: SupportedSession;
  onLookupError?: (errorType: ErrorType, errorString?: string) => void;
  onLookupSuccess?: () => void;
  onLinkSuccess?: () => void;
  onLinkError?: () => void;
}

export const useLinkScriptSurePrescriberId = ({
  session,
  onLookupError,
  onLookupSuccess,
  onLinkSuccess,
  onLinkError,
}: UseLinkScriptSurePrescriberIdOptions = {}) => {
  const { mutateAsync: updateUser, isPending: isLinking } = useTellescopeMutation({
    model: "users",
    method: "updateOne",
    onSuccess: onLinkSuccess,
    onError: onLinkError,
  });
  const { fetchQuery } = useTellescopeQueryClient(session);
  const [isLookingUp, setIsLookingUp] = useState(false);

  const linkScriptSurePrescriberId = async (userId: string) => {
    setIsLookingUp(true);
    if (!userId) {
      onLookupError?.("no-scriptsure-user-found");
      setIsLookingUp(false);
      return;
    }
    try {
      const lookupResult = await fetchQuery({
        model: "integrations",
        method: "proxy_read",
        request: {
          integration: "ScriptSure",
          type: "user-lookup",
          id: userId,
        },
      });
      if (isScriptSureUserLookupResponse(lookupResult)) {
        if (lookupResult.data.error) {
          const errorType = convertErrorStringToErrorType(lookupResult.data.error);
          onLookupError?.(errorType, lookupResult.data.error);
          setIsLookingUp(false);
          return;
        }

        if (lookupResult.data.prescriber && lookupResult.data.scriptsureId) {
          onLookupSuccess?.();
          await updateUser(
            {
              id: userId,
              scriptSurePrescriberId: lookupResult.data.scriptsureId,
            },
            {
              onSuccess: onLinkSuccess,
              onError: onLinkError,
            }
          );
        } else {
          onLookupError?.("no-scriptsure-user-found");
        }
      }
    } catch {
      onLookupError?.("unknown");
    } finally {
      setIsLookingUp(false);
    }
  };

  return {
    linkScriptSurePrescriberId,
    isLookingUp,
    isLinking,
    isPending: isLookingUp || isLinking,
  };
};
